import classnames from 'classnames';
import { useContext, useEffect, useRef } from 'react';
import * as React from 'react';
import { Modal, ModalBox, ModalContext } from 'palette/Modal';
import { Transition } from 'palette/Transition';
import { ANCHOR, SIZE, HEIGHT } from './constants';
import './styles/Drawer.scss';

type DrawerProps = React.PropsWithChildren<{
    id: string;
    className?: string;
    anchor?: ANCHOR;
    modalBoxClassname?: string;
    rounded?: boolean;
    size?: SIZE;
    height?: HEIGHT;
    onClose?(): void;
}>;

function Drawer({
    children,
    id,
    anchor = 'left',
    className,
    modalBoxClassname,
    rounded = false,
    size = 'default',
    height,
    onClose,
}: DrawerProps) {
    const { activeModalId } = useContext(ModalContext);
    const isOpen = id === activeModalId;

    const classes = classnames(className, 'palette-drawer');
    const drawerBoxRootClass = 'palette-drawer__drawer-box';
    const drawerBoxClasses = classnames(
        drawerBoxRootClass,
        `${drawerBoxRootClass}--${anchor}`,
        `${drawerBoxRootClass}--${size}`,
        { [`${drawerBoxRootClass}--height-${height}`]: height },
        modalBoxClassname,
    );

    const modalBoxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            if (!modalBoxRef.current) { return; }

            const { offsetHeight } = modalBoxRef.current;
            const fullHeightBreakpoint = 0.85;

            const heightPercentageOfWindow = (offsetHeight / window.innerHeight);

            if (heightPercentageOfWindow >= fullHeightBreakpoint) {
                modalBoxRef.current.classList.add('palette-drawer--fullHeight');
            } else {
                modalBoxRef.current.classList.remove('palette-drawer--fullHeight');
            }
        }
    }, [isOpen]);

    return (
        <Modal
            className={classes}
            id={id}
            onClose={onClose}
        >
            <Transition
                appear
                classNames={`palette-drawer__drawer-box--${anchor}`}
                property="transform"
                show={isOpen}
                timeout="quick"
            >
                <ModalBox
                    ref={modalBoxRef}
                    className={drawerBoxClasses}
                    rounded={rounded}
                >
                    {children}
                </ModalBox>
            </Transition>
        </Modal>
    );
}

export default Drawer;
